import { Plus, Trash2 } from "lucide-react";
import { supabase } from "@/project/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export type MemberValues = {
  full_name: string;
  relation: string;
  gender: string;
  age: string;
  mobile: string;
  aadhaar: string;
  occupation: string;
};

export type SadharmikValues = {
  id?: string;
  ration_card_no: string;
  address: string;
  area: string;
  city: string;
  pincode: string;
  primary_mobile: string;
  alt_mobile: string;
  notes: string;
  members: MemberValues[];
};

export const emptyMember = (): MemberValues => ({
  full_name: "",
  relation: "",
  gender: "",
  age: "",
  mobile: "",
  aadhaar: "",
  occupation: "",
});

export const emptySadharmik = (): SadharmikValues => ({
  ration_card_no: "",
  address: "",
  area: "",
  city: "Mumbai",
  pincode: "",
  primary_mobile: "",
  alt_mobile: "",
  notes: "",
  members: [{ ...emptyMember(), relation: "Self" }],
});

export function validateSadharmik(v: SadharmikValues): string | null {
  const head = v.members[0];
  if (!head?.full_name.trim()) {
    return "Member name is required";
  }
  if (!v.primary_mobile.trim()) {
    return "Mobile number is required";
  }
  return null;
}

/** Loads an existing household plus its members into editable form values. */
export async function loadSadharmik(
  familyId: string
): Promise<SadharmikValues> {
  const [{ data: family, error }, { data: members }] = await Promise.all([
    supabase.from("families").select("*").eq("id", familyId).single(),
    supabase
      .from("family_members")
      .select("*")
      .eq("family_id", familyId)
      .order("created_at"),
  ]);
  if (error) {
    throw error;
  }
  return {
    id: family.id,
    ration_card_no: family.ration_card_no ?? "",
    address: family.address ?? "",
    area: family.area ?? "",
    city: family.city ?? "",
    pincode: family.pincode ?? "",
    primary_mobile: family.primary_mobile ?? "",
    alt_mobile: family.alt_mobile ?? "",
    notes: family.notes ?? "",
    members: (members ?? []).length
      ? (members ?? []).map(m => ({
          full_name: m.full_name ?? "",
          relation: m.relation ?? "",
          gender: m.gender ?? "",
          age: m.age !== null ? String(m.age) : "",
          mobile: m.mobile ?? "",
          aadhaar: m.aadhaar ?? "",
          occupation: m.occupation ?? "",
        }))
      : [{ ...emptyMember(), relation: "Self" }],
  };
}

/** Creates (or reuses) the family record and stores the member list. */
export async function saveSadharmik(v: SadharmikValues, userId?: string) {
  const rc =
    v.ration_card_no.trim().toUpperCase() ||
    `SDK-${Date.now().toString(36).toUpperCase()}`;
  const members = v.members.filter(m => m.full_name.trim());

  const { data: matched } = await supabase
    .from("families")
    .select("id")
    .eq("ration_card_no", rc)
    .maybeSingle();
  const existing = v.id ? { id: v.id } : matched;

  const payload = {
    ration_card_no: rc,
    address: v.address || null,
    area: v.area || null,
    city: v.city || null,
    pincode: v.pincode || null,
    primary_mobile: v.primary_mobile || null,
    alt_mobile: v.alt_mobile || null,
    member_count: members.length,
    notes: v.notes || null,
    updated_by: userId ?? null,
  };

  let familyId = existing?.id;
  if (familyId) {
    const { error } = await supabase
      .from("families")
      .update(payload)
      .eq("id", familyId);
    if (error) {
      throw error;
    }
    await supabase.from("family_members").delete().eq("family_id", familyId);
  } else {
    const { data, error } = await supabase
      .from("families")
      .insert({ ...payload, created_by: userId ?? null })
      .select("id")
      .single();
    if (error) {
      throw error;
    }
    familyId = data.id;
  }

  if (members.length) {
    const { error } = await supabase.from("family_members").insert(
      members.map(m => ({
        family_id: familyId!,
        full_name: m.full_name.trim(),
        relation: m.relation || null,
        gender: m.gender || null,
        age: m.age ? Number(m.age) : null,
        mobile: m.mobile || null,
        aadhaar: m.aadhaar || null,
        occupation: m.occupation || null,
      }))
    );
    if (error) {
      throw error;
    }
  }

  return { familyId: familyId!, rationCard: rc };
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

export function SadharmikForm({
  value,
  onChange,
}: {
  value: SadharmikValues;
  onChange: (v: SadharmikValues) => void;
}) {
  const set = <K extends keyof SadharmikValues>(k: K, v: SadharmikValues[K]) =>
    onChange({ ...value, [k]: v });

  const setMember = (i: number, k: keyof MemberValues, v: string) => {
    const members = value.members.map((m, idx) =>
      idx === i ? { ...m, [k]: v } : m
    );
    onChange({ ...value, members });
  };

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Member Details</CardTitle>
          <p className="text-xs text-muted-foreground">
            Only name and mobile number are required to register. Everything
            else can be filled in later.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <Field label="Member Name *">
            <Input
              value={value.members[0]?.full_name ?? ""}
              placeholder="Full name of the member"
              onChange={e => setMember(0, "full_name", e.target.value)}
            />
          </Field>
          <Field label="Mobile *">
            <Input
              inputMode="numeric"
              maxLength={15}
              placeholder="10-digit mobile number"
              value={value.primary_mobile}
              onChange={e => set("primary_mobile", e.target.value)}
            />
          </Field>
          <Field label="Alternate Mobile">
            <Input
              inputMode="numeric"
              maxLength={15}
              placeholder="Optional"
              value={value.alt_mobile}
              onChange={e => set("alt_mobile", e.target.value)}
            />
          </Field>
          <Field label="Ration Card No. (optional)">
            <Input
              value={value.ration_card_no}
              placeholder="Leave blank if not available"
              onChange={e =>
                set("ration_card_no", e.target.value.toUpperCase())
              }
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Residential Address (optional)">
              <Textarea
                rows={2}
                placeholder="Building, street, landmark"
                value={value.address}
                onChange={e => set("address", e.target.value)}
              />
            </Field>
          </div>
          <Field label="Area / Locality">
            <Input
              placeholder="e.g. Matunga"
              value={value.area}
              onChange={e => set("area", e.target.value)}
            />
          </Field>
          <Field label="City">
            <Input
              value={value.city}
              onChange={e => set("city", e.target.value)}
            />
          </Field>
          <Field label="Pincode">
            <Input
              inputMode="numeric"
              maxLength={6}
              placeholder="400019"
              value={value.pincode}
              onChange={e => set("pincode", e.target.value)}
            />
          </Field>
          <div className="md:col-span-3">
            <Field label="Notes">
              <Textarea
                rows={2}
                placeholder="Any internal remark about this member / household"
                value={value.notes}
                onChange={e => set("notes", e.target.value)}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Family Details</CardTitle>
            <p className="text-xs text-muted-foreground">
              Optional now — you can update family member details any time later
              from Member Master.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            type="button"
            onClick={() =>
              onChange({ ...value, members: [...value.members, emptyMember()] })
            }
          >
            <Plus className="mr-1 h-4 w-4" /> Add Member
          </Button>
        </CardHeader>
        <CardContent className="grid gap-4">
          {value.members.map((m, i) => (
            <div key={i} className="rounded-md border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {i === 0 ? "Head of family" : `Family member ${i + 1}`}
                </span>
                {value.members.length > 1 && (
                  <Button
                    size="icon"
                    variant="ghost"
                    type="button"
                    title="Remove this family member"
                    aria-label={`Remove family member ${i + 1}`}
                    onClick={() =>
                      onChange({
                        ...value,
                        members: value.members.filter((_, x) => x !== i),
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="lg:col-span-2">
                  <Field label="Full Name">
                    <Input
                      placeholder="Full name"
                      value={m.full_name}
                      onChange={e => setMember(i, "full_name", e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Relation">
                  <Input
                    placeholder="Self / Wife / Son"
                    value={m.relation}
                    onChange={e => setMember(i, "relation", e.target.value)}
                  />
                </Field>
                <Field label="Gender">
                  <Input
                    placeholder="Male / Female"
                    value={m.gender}
                    onChange={e => setMember(i, "gender", e.target.value)}
                  />
                </Field>
                <Field label="Age">
                  <Input
                    inputMode="numeric"
                    maxLength={3}
                    placeholder="Years"
                    value={m.age}
                    onChange={e => setMember(i, "age", e.target.value)}
                  />
                </Field>
                <Field label="Mobile">
                  <Input
                    inputMode="numeric"
                    maxLength={15}
                    placeholder="Optional"
                    value={m.mobile}
                    onChange={e => setMember(i, "mobile", e.target.value)}
                  />
                </Field>
                <Field label="Aadhaar">
                  <Input
                    inputMode="numeric"
                    maxLength={14}
                    placeholder="12-digit number"
                    value={m.aadhaar}
                    onChange={e => setMember(i, "aadhaar", e.target.value)}
                  />
                </Field>
                <Field label="Occupation">
                  <Input
                    placeholder="Work / study"
                    value={m.occupation}
                    onChange={e => setMember(i, "occupation", e.target.value)}
                  />
                </Field>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
